import React, { useMemo } from "react";
import { Modal, Typography, message } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AgentPageHeader } from "../../components/agent/AgentPageHeader";
import {
  fetchAgentContainerById,
  linkMultipleBookingsToContainer,
} from "../../services/agentPortalService";
import {
  AgentBookingsList,
} from "../../components/agent/AgentBookingsList";
import { AgentConsolidationBar } from "../../components/agent/AgentConsolidationBar";
import { AGENT_ROUTES } from "../../config/agentPortal";
import { useAgentBookingsPage } from "../../hooks/useAgentBookingsPage";
import { agentBookingsDetailNavigateOptions } from "../../utils/agentBookingsNavigation";
import {
  bulkAnalyzeStoredCommercialInvoices,
  getPrimaryCommercialInvoicePath,
} from "../../features/commercialInvoiceAnalysis/commercialInvoiceAnalysisService";
import { bookingNeedsCiAnalysis } from "../../utils/agentBookingCiAnalysis";
import { Booking } from "../../types/booking";

const { Text } = Typography;

const bookingLabel = (booking: Booking) =>
  booking.reference
    ? `${booking.reference} (#${booking.id})`
    : `Booking #${booking.id}`;

const containerLabelForBooking = (booking: Booking) => {
  const info = Array.isArray(booking.container_info)
    ? booking.container_info[0]
    : booking.container_info;
  return info?.reference || `#${booking.container_id}`;
};

const AgentBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    agent,
    profileLoading,
    bookings,
    loading,
    filters,
    searchMode,
    listTruncated,
    selectedRowKeys,
    setSelectedRowKeys,
    companies,
    transportTypes,
    bookingTypes,
    containers,
    masters,
    loadingOptions,
    hasActiveFilters,
    statusCounts,
    handleStatusChange,
    handleSearchChange,
    handleCompanyChange,
    handleTransportChange,
    handleHasContainerChange,
    handleContainerChange,
    handleMasterContainerChange,
    handleBookingTypeChange,
    handleServiceLevelChange,
    handlePickupFilterChange,
    handleClearFilters,
    handleRefresh,
    reloadAfterMutation,
    agentId,
  } = useAgentBookingsPage();

  const [consolidating, setConsolidating] = React.useState(false);
  const [analysingCi, setAnalysingCi] = React.useState(false);

  const selectedBookings = useMemo(
    () => bookings.filter((b) => selectedRowKeys.includes(b.id)),
    [bookings, selectedRowKeys]
  );

  const selectedMissingCi = useMemo(
    () => selectedBookings.filter(bookingNeedsCiAnalysis),
    [selectedBookings]
  );

  const handleBulkAnalyseCi = async () => {
    if (selectedMissingCi.length === 0) return;

    setAnalysingCi(true);
    const progressKey = "bulk-ci-analysis";

    try {
      const items = selectedMissingCi
        .map((booking) => ({
          bookingId: booking.id,
          objectPath: getPrimaryCommercialInvoicePath(booking.commercial_invoice_file)!,
        }))
        .filter((item) => item.objectPath);

      message.loading({
        content: `Analysing 0/${items.length} invoices…`,
        key: progressKey,
        duration: 0,
      });

      const result = await bulkAnalyzeStoredCommercialInvoices(
        items,
        (completed, total) => {
          message.loading({
            content: `Analysing ${completed}/${total} invoices…`,
            key: progressKey,
            duration: 0,
          });
        }
      );

      if (result.succeeded > 0 && result.failed === 0) {
        message.success({
          content: `Analysed ${result.succeeded} commercial invoice${result.succeeded === 1 ? "" : "s"}`,
          key: progressKey,
        });
      } else if (result.succeeded > 0) {
        message.warning({
          content: `Analysed ${result.succeeded}, ${result.failed} failed`,
          key: progressKey,
        });
      } else {
        message.error({
          content: `${result.failed} analysis${result.failed === 1 ? "" : "es"} failed`,
          key: progressKey,
        });
      }

      await reloadAfterMutation();
    } catch (err) {
      message.error({ content: "Bulk CI analysis failed", key: progressKey });
      console.error(err);
    } finally {
      setAnalysingCi(false);
    }
  };

  const linkBookingsToContainer = async (
    bookingIds: number[],
    container: { id: number; reference: string | null }
  ) => {
    setConsolidating(true);
    try {
      const { success, error } = await linkMultipleBookingsToContainer(
        bookingIds,
        container.id
      );
      if (error || !success) throw error;
      message.success(
        `Added ${bookingIds.length} booking(s) to ${container.reference || container.id}`
      );
      setSelectedRowKeys([]);
      await reloadAfterMutation();
      return true;
    } catch (err) {
      console.error(err);
      message.error("Failed to add bookings to consolidation");
      return false;
    } finally {
      setConsolidating(false);
    }
  };

  /** @returns true when bookings were linked (caller may clear container select). */
  const handleConsolidate = async (containerId: number): Promise<boolean> => {
    if (!agentId || selectedRowKeys.length === 0) return false;

    const { data: container, error: findError } =
      await fetchAgentContainerById(containerId);
    if (findError || !container) {
      message.error("Container not found");
      return false;
    }

    const bookingIds = selectedRowKeys.map(Number);
    const owned = bookings.filter(
      (b) => bookingIds.includes(b.id) && b.agent_id === agentId
    );
    if (owned.length !== bookingIds.length) {
      message.error("Some selected bookings are not assigned to you");
      return false;
    }

    const alreadyConsolidated = owned.filter((b) => b.container_id != null);
    const linkable = owned.filter((b) => b.container_id == null);

    if (alreadyConsolidated.length === 0) {
      return linkBookingsToContainer(
        linkable.map((b) => b.id),
        container
      );
    }

    const skippedList = (
      <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
        {alreadyConsolidated.map((b) => (
          <li key={b.id}>
            {bookingLabel(b)} — already in{" "}
            <Text strong>{containerLabelForBooking(b)}</Text>
          </li>
        ))}
      </ul>
    );

    if (linkable.length === 0) {
      Modal.warning({
        title: "Already in a consolidation",
        content: (
          <div>
            <p style={{ marginBottom: 0 }}>
              All selected bookings are already linked to a container. Remove
              them from their current consolidation first, or select other
              bookings.
            </p>
            {skippedList}
          </div>
        ),
      });
      return false;
    }

    return new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: "Some bookings are already consolidated",
        content: (
          <div>
            <p style={{ marginBottom: 0 }}>
              The following booking
              {alreadyConsolidated.length === 1 ? " is" : "s are"} already in a
              consolidation and will be skipped:
            </p>
            {skippedList}
            <p style={{ marginTop: 12, marginBottom: 0 }}>
              Add the remaining {linkable.length} booking
              {linkable.length === 1 ? "" : "s"} to{" "}
              <Text strong>{container.reference || `#${container.id}`}</Text>?
            </p>
          </div>
        ),
        okText: `Add ${linkable.length} booking${linkable.length === 1 ? "" : "s"}`,
        cancelText: "Cancel",
        onOk: async () => {
          const ok = await linkBookingsToContainer(
            linkable.map((b) => b.id),
            container
          );
          resolve(ok);
          if (!ok) return Promise.reject(new Error("link failed"));
        },
        onCancel: () => resolve(false),
      });
    });
  };

  if (profileLoading) return null;

  return (
    <div
      style={{
        paddingBottom: selectedRowKeys.length > 0 ? 120 : 0,
      }}
    >
      <AgentPageHeader title="Bookings" agentName={agent?.name} />
      <AgentBookingsList
        bookings={bookings}
        loading={loading}
        statusFilter={filters.status}
        search={filters.q}
        searchMode={searchMode}
        listTruncated={listTruncated}
        companyId={filters.companyId}
        transportTypeId={filters.transportTypeId}
        hasContainer={filters.hasContainer}
        containerId={filters.containerId}
        masterContainerId={filters.masterContainerId}
        bookingTypeId={filters.bookingTypeId}
        serviceLevel={filters.serviceLevel}
        pickupFilter={filters.pickupFilter}
        companies={companies}
        transportTypes={transportTypes}
        bookingTypes={bookingTypes}
        containers={containers}
        masters={masters}
        loadingOptions={loadingOptions}
        hasActiveFilters={hasActiveFilters}
        statusCounts={statusCounts}
        onStatusChange={handleStatusChange}
        onSearchChange={handleSearchChange}
        onCompanyChange={handleCompanyChange}
        onTransportChange={handleTransportChange}
        onHasContainerChange={handleHasContainerChange}
        onContainerChange={handleContainerChange}
        onMasterContainerChange={handleMasterContainerChange}
        onBookingTypeChange={handleBookingTypeChange}
        onServiceLevelChange={handleServiceLevelChange}
        onPickupFilterChange={handlePickupFilterChange}
        onClearFilters={handleClearFilters}
        onRefresh={handleRefresh}
        onOpenBooking={(id) =>
          navigate(
            AGENT_ROUTES.bookingDetail(id),
            agentBookingsDetailNavigateOptions(searchParams.toString())
          )
        }
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
      />
      <AgentConsolidationBar
        selectedCount={selectedRowKeys.length}
        missingCiCount={selectedMissingCi.length}
        onAnalyseCi={handleBulkAnalyseCi}
        analysingCi={analysingCi}
        onConsolidate={handleConsolidate}
        consolidating={consolidating}
        onClearSelection={() => setSelectedRowKeys([])}
      />
    </div>
  );
};

export default AgentBookingsPage;
